import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema } from './primitives.js';
const c = initContract();
/**
 * Journal Entries — FIN-02: the manual journal workflow over the ledger
 * engine's tables.
 *
 * Status model. The database keeps R8's three statuses (draft / posted /
 * reversed) plus a separate approval axis on drafts; the wire exposes the
 * DERIVED display status the screen's §7 chips draw:
 *   draft            — a plain working paper (approval axis empty)
 *   pending_approval — submitted, waiting (a DRAFT sub-state per R8)
 *   approved         — the §7 state diagram's "(approved)": may be posted
 *   rejected         — an approver said no and said why (a DRAFT sub-state)
 *   posted           — it counts; locked forever
 *   reversed         — a REVERSING entry (the original it undoes stays posted)
 *
 * Permissions (accounting area, per FIN-02 §9 resolved through PART 16, which
 * wins — 16.5: "accounting.approve for posting a journal"; 16.3: Approve
 * covers reversing too):
 *   read = accounting.view · create a draft = accounting.create · edit a
 *   draft + submit = accounting.edit · delete a draft = accounting.delete ·
 *   approve/reject = accounting.approve, and NOT the submitter (§9) ·
 *   post = accounting.approve · reverse = accounting.approve.
 *
 * The submitter-may-not-approve rule yields to the Owner flag: PART 16 reads
 * the flag before everything else ("nobody can shut me out"), and a
 * one-person company could otherwise never post a manual journal.
 *
 * R8 everywhere: drafts (including pending/rejected) freely editable and
 * deletable — an edit returns the entry to Draft and clears the approval
 * (Q-FIN-02-4); posted entries refuse every edit AT THE SERVER
 * (BR-FIN-02-08) and are corrected only by reverse (BR-FIN-02-09/-10).
 */
// ── The fixed sentences of FIN-02 §6/§7/§10/§14 ─────────────────────────────
export const JOURNAL_MESSAGES = {
    /** BR-FIN-02-01 / ERR-FIN-02-01, quoted exactly. */
    unbalanced: 'Debits must equal credits before posting.',
    /** BR-FIN-02-05 / ERR-FIN-02-02, quoted exactly. */
    notApproved: 'Entry must be approved before it can be posted.',
    /** BR-FIN-02-02. */
    empty: 'An empty journal entry can never be posted.',
    /** The §7 posted locked notice, quoted exactly — ERR-FIN-02-05's read-only cells and their reason. */
    postedLocked: 'This entry is posted and locked. Reverse it to make corrections — posted entries cannot be edited or deleted.',
    /** The §7 reversing notice, quoted exactly. */
    reversingNotice: 'This is a reversing entry that offsets the original, preserving the audit trail.',
    /** The §7 auto-generated notice, quoted exactly — ERR-FIN-02-06. */
    autoNotice: 'System-generated from a source document. Lines are read-only; use Reverse entry for corrections.',
    /**
     * BR-FIN-02-07 — the API refusal behind the notice, and the server half of
     * ERR-FIN-02-08 (the Submit button is not drawn on an auto-generated entry;
     * this is what happens when the route is called anyway).
     */
    autoLocked: 'System-generated entries cannot be edited or submitted. Correct the source document instead.',
    /** FIN-02 §9 / Q-FIN-02-5. */
    selfApproval: 'A journal entry must not be approved by the person who submitted it.',
    submitManualDraftOnly: 'Only a manual draft entry can be submitted for approval.',
    alreadyPending: 'This entry is already pending approval.',
    alreadyApproved: 'This entry is already approved. Post it.',
    notPending: 'Only an entry pending approval can be approved or rejected.',
    /** A3 — the reason box is required on a rejection. */
    rejectionReasonRequired: 'Explain why this entry was rejected.',
    /** BR-FIN-02-10. */
    onlyPostedReverses: 'Only a posted entry can be reversed.',
    alreadyReversed: 'This entry has already been reversed. Both entries remain in the books.',
    /**
     * A23 (DELEGATED RULINGS, 26 Aug 2026) — "journal Reverse → asks, with a
     * typed reason, joining both vouchers."
     *
     * The third member of a family, and deliberately word-for-word its two
     * siblings with the document noun swapped:
     *
     *   RECEIPT_MESSAGES.reverseReasonRequired
     *     'A posted receipt can only be reversed with a reason, …'
     *   PAYMENT_MESSAGES.reverseReasonRequired
     *     'A posted payment can only be reversed with a reason, …'
     *
     * IT MUST STAY BYTE-IDENTICAL TO `je.errReverseReasonRequired` in both
     * dictionaries, because `server-messages.ts` maps whole sentences to i18n
     * keys BY EXACT MATCH — the map key is this constant, so a rewording here
     * that misses the dictionary leaves an Arabic reader meeting English. It
     * carries NO parameter, which is what makes an exact-match entry possible
     * at all; anything interpolated would need an ICU key instead.
     * `journal-reversal-ask.test.tsx` asserts the byte-identity in both
     * languages so the pair cannot drift quietly.
     */
    reverseReasonRequired: 'A posted entry can only be reversed with a reason, which is written to the audit log.',
    /**
     * A receipt's or payment's entry is not reversed HERE: the voucher's
     * settlement state (which invoices count as paid) rides the voucher's own
     * reversal stamp, so flipping the entry alone would restore the ledger and
     * leave every allocated invoice still marked settled — the books and the
     * subledger disagreeing. One door per correction (the same reason FIN-03/
     * FIN-04 do not offer credit notes): the voucher's Reverse action does both
     * halves in one transaction.
     */
    voucherOwnsEntry: (voucherCode) => `This entry was posted by voucher ${voucherCode}. Reverse the voucher itself — reversing its entry alone would leave the invoices or bills it settled still marked as paid.`,
    /** R8 — no delete route out of Posted (BR-PERM-02's sentence family). */
    postedNoDelete: 'A posted entry cannot be deleted. Reverse it instead.',
    notFound: 'This journal entry does not exist.',
};
/**
 * D46 — the voucher-owned-entry refusal, structured. `voucherCode` is a display
 * value. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const JOURNAL_REFUSALS = {
    voucherOwnsEntry: (voucherCode) => ({
        message: JOURNAL_MESSAGES.voucherOwnsEntry(voucherCode),
        key: 'je.errVoucherOwnsEntry',
        params: { voucherCode },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const JournalStatusSchema = z.enum([
    'draft',
    'pending_approval',
    'approved',
    'rejected',
    'posted',
    'reversed',
]);
export const JournalLineSchema = z
    .object({
    lineNo: z.number().int(),
    accountId: z.uuid(),
    accountCode: z.string(),
    accountName: z.string(),
    accountNameAr: z.string(),
    debit: MoneyStringSchema,
    credit: MoneyStringSchema,
    currency: z.string(),
    memo: z.string().nullable(),
})
    .strict();
/** Who did a workflow step — id plus the displayable email. */
const ActorSchema = z.object({ id: z.uuid(), email: z.string() }).strict().nullable();
/** A link to a related entry (the reversal pair, both directions). */
const EntryLinkSchema = z
    .object({ id: z.uuid(), entryNo: z.number().int(), code: z.string() })
    .strict()
    .nullable();
/**
 * A15 / BR-FIN-02-12 — every KIND of act that can raise a journal entry.
 *
 * This list is the ledger's vocabulary and the screen's route table at once,
 * which is why it lives in the contract rather than in either of them: the
 * posting door names a member when it stamps, the register and the editor turn
 * the member into a word (`je.source.<type>`) and a path, and TypeScript makes
 * both halves exhaustive. Adding a source kind is therefore a change with
 * exactly three compile errors and no silent ones.
 *
 * IT IS A KIND OF ACT, NOT A TABLE NAME. Three members deliberately do not
 * correspond to the row the pointer holds:
 *   `fixed_asset`  — a disposal's link goes to the ASSET, because FIN-07 has
 *     an asset page and no disposal page and BR-FIN-02-12 asks for a link that
 *     WORKS.
 *   `bank_reconciliation` — the adjustment pointer lives on a reconciliation
 *     LINE; a reader following it wants the statement, not one row of one.
 *   `journal_entry` — a reversal's source is the entry it reverses. FIN-02
 *     §5a names it as the fourth kind of source in as many words, so this is
 *     the spec's own list and not a convenience.
 *
 * Two members can never carry an `id`, and that is the shape rather than an
 * omission: ADM-03's opening-balance import leaves no row behind anywhere, and
 * INV-06's manual `ADJ-M<n>` movement is, in `stock.service.ts`'s own words,
 * "the one path where a unit enters or leaves the ledger with no document
 * behind it". Both are NAMED and deliberately not linked — which is a better
 * answer than a link that silently goes nowhere.
 *
 * `drawer_deposit` is SAL-06 §12's "Drawer banked at close" (delegated ruling
 * A21), added when the four doors A15 deferred were stamped. It has a row —
 * the `pos_shifts` shift whose close raised it — and no number, because a
 * shift has no number: it is absent from `app_number_series_catalogue()` and
 * carries no `shift_no` column. So it takes an `id` and leaves `code` NULL,
 * on `bank_reconciliation`'s precedent, rather than borrowing the REGISTER's
 * code — which names the till the drawer belongs to and would read in the
 * Source cell as a document number this act does not have.
 */
export const JOURNAL_SOURCE_TYPES = [
    'invoice',
    'credit_note',
    'delivery_note',
    'pos_sale',
    'purchase_invoice',
    'purchase_return',
    'goods_receipt',
    'import_order',
    'receipt',
    'payment',
    'check',
    'cash_transfer',
    'bank_reconciliation',
    'stock_adjustment',
    'stock_count',
    'stock_transfer',
    'stock_movement',
    'work_order',
    'material_issue',
    'payroll_run',
    'depreciation_run',
    'fixed_asset',
    'revaluation',
    'opening_balance_import',
    'journal_entry',
    'drawer_deposit',
];
export const JournalSourceTypeSchema = z.enum(JOURNAL_SOURCE_TYPES);
/**
 * What raised this entry — the three facts stored on the entry itself.
 *
 * `id` and `code` are independently nullable and mean different things. A NULL
 * `id` is "this act has no page to open" (the two members above). A NULL
 * `code` is "the number was not recorded": true of every entry the
 * 20260913100000 backfill described, because a migration has no tenant context
 * in which to format one and a plausible number is manufactured provenance.
 *
 * The whole object is NULL for a hand-typed entry — and for an automatic entry
 * predating the migration whose source no table could reach, where the screen
 * draws ruling D28's "Not recorded". Those two are told apart by
 * `autoGenerated`, which is why this field never had to encode the difference.
 */
export const JournalSourceSchema = z
    .object({
    type: JournalSourceTypeSchema,
    id: z.uuid().nullable(),
    code: z.string().nullable(),
})
    .strict()
    .nullable();
export const JournalEntrySchema = z
    .object({
    id: z.uuid(),
    entryNo: z.number().int(),
    /**
     * JE-#### as this company prints it.
     *
     * This used to say "display pattern JE-#### is the UI's job; the number
     * itself travels", and the number did travel — into a `` `JE-${entryNo}` ``
     * in five places in the browser. MD-13 makes the prefix and the width a
     * TENANT'S SETTING held in `number_sequences`, and a browser cannot read
     * one: a company that renames its entries would have gone on seeing JE- on
     * the register for ever. So the finished code travels instead, formatted
     * where the setting lives.
     */
    code: z.string(),
    entryDate: IsoDateSchema,
    /** Field 5 — Reference / description (the entry memo). */
    reference: z.string().nullable(),
    status: JournalStatusSchema,
    autoGenerated: z.boolean(),
    /** On a reversing entry: the original it undoes (BR-FIN-02-09 §4). */
    reversalOf: EntryLinkSchema,
    /** On a reversed original: the entry that undid it. */
    reversedBy: EntryLinkSchema,
    /**
     * A23 — WHY the pair exists, readable from EITHER end of it.
     *
     * The column lives on the REVERSING entry (beside `reversal_of`), because
     * R8 keeps the original untouched — "the ORIGINAL IS NEVER MUTATED" is
     * `reverse.ts`'s own sentence and the whole reason a reversal is a second
     * entry rather than an edit. But the reader most likely to be asking why
     * something was undone is the one who opened the ORIGINAL, so this field
     * resolves in both directions: it is the reversing entry's own reason
     * whichever half of the pair you fetched.
     *
     * NULL is a real state, not a placeholder: every entry reversed before
     * A23 landed has one, exactly as a bounce-stamped voucher's
     * `reversal_reason` is NULL. The screen shows the notice and no Reason
     * line — "Reason: " with nothing after it is worse than silence.
     */
    reversalReason: z.string().nullable(),
    /** A15 / BR-FIN-02-12 — FIN-02 §5a field 6, the Source link. */
    source: JournalSourceSchema,
    submittedBy: ActorSchema,
    approvedBy: ActorSchema,
    /** A2 — the green chip, date-only. */
    approvedAt: IsoDateSchema.nullable(),
    rejectionReason: z.string().nullable(),
    postedAt: z.string().nullable(),
    createdBy: z.uuid().nullable(),
    lines: JournalLineSchema.array(),
    totalDebit: MoneyStringSchema,
    totalCredit: MoneyStringSchema,
})
    .strict();
/** The list row — no lines, but the totals the register shows. */
export const JournalListRowSchema = z
    .object({
    id: z.uuid(),
    entryNo: z.number().int(),
    /** JE-#### as this company prints it — see JournalEntrySchema.code. */
    code: z.string(),
    entryDate: IsoDateSchema,
    reference: z.string().nullable(),
    status: JournalStatusSchema,
    autoGenerated: z.boolean(),
    /**
     * A15 — the register's own Source column.
     *
     * It is read straight off the three columns with NO JOIN, which is the
     * whole reason `source_code` is stored rather than resolved: a live code
     * would put twenty-odd LEFT JOINs on the page query, once per row.
     */
    source: JournalSourceSchema,
    lineCount: z.number().int(),
    totalDebit: MoneyStringSchema,
    totalCredit: MoneyStringSchema,
})
    .strict();
const NonZeroMoneySchema = MoneyStringSchema.refine((value) => !/^0+(\.0+)?$/.test(value), 'An amount must be above zero — money moves the other way by using the other column.');
/**
 * One line as typed: exactly one side, above zero (BR-FIN-02-03; the §15
 * negative-amount refusal is the schema's non-negative regex).
 */
export const JournalLineInputSchema = z
    .object({
    accountId: z.uuid(),
    debit: NonZeroMoneySchema.optional(),
    credit: NonZeroMoneySchema.optional(),
    /**
     * Per-line currency, defaulting to the base — the input half of the
     * per-currency ledger FIN-02 §5b already stores and returns ("the
     * balance rule holds PER CURRENCY", BR-FIN-02-01). This is how a
     * foreign balance is booked so FIN-05's revaluation has an FC leg to
     * measure (its §5d table derives from these lines).
     */
    currency: z.string().regex(/^[A-Z]{3}$/).optional(),
    memo: z.string().max(500).nullish(),
})
    .strict()
    .refine((line) => (line.debit === undefined) !== (line.credit === undefined), 'A line must hold either a debit or a credit, never both.');
/**
 * The draft payload — saved wholesale on every edit (R8). An unbalanced
 * draft SAVES (it is a working paper); balance is enforced at approval and
 * posting (BR-FIN-02-01/-06).
 */
export const SaveJournalSchema = z
    .object({
    entryDate: IsoDateSchema,
    reference: z.string().max(500).nullish(),
    lines: JournalLineInputSchema.array().min(1),
})
    .strict();
// ── Routes ──────────────────────────────────────────────────────────────────
export const journalsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/journals',
        query: pagedQuery({
            status: JournalStatusSchema.optional(),
            /** Inclusive entry-date bounds — the period filter. */
            from: IsoDateSchema.optional(),
            to: IsoDateSchema.optional(),
        }),
        responses: {
            200: pagedResponse('entries', JournalListRowSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The journal register, newest first, with period/status filters (accounting.view), PAGED (NFR-42). `status` is DERIVED from status + approval_status and is filtered by the same expression in SQL, not after the fact.',
    },
    get: {
        method: 'GET',
        path: '/api/journals/:entryId',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: {
            200: JournalEntrySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One entry with its lines and approval trail (accounting.view).',
    },
    create: {
        method: 'POST',
        path: '/api/journals',
        body: SaveJournalSchema,
        responses: {
            201: JournalEntrySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Create a MANUAL draft entry (accounting.create).',
    },
    update: {
        method: 'PUT',
        path: '/api/journals/:entryId',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        body: SaveJournalSchema,
        responses: {
            200: JournalEntrySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Rewrite a draft wholesale (accounting.edit). Returns it to Draft, clearing any approval (Q-FIN-02-4).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/journals/:entryId',
        body: c.noBody(),
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a draft outright (accounting.delete). Posted entries refuse (R8).',
    },
    submit: {
        method: 'POST',
        path: '/api/journals/:entryId/submit',
        body: c.noBody(),
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: {
            200: JournalEntrySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Draft → Pending approval, manual entries only (accounting.edit).',
    },
    approve: {
        method: 'POST',
        path: '/api/journals/:entryId/approve',
        body: c.noBody(),
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: {
            200: JournalEntrySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Approve a pending, BALANCED entry (accounting.approve; not the submitter — FIN-02 §9).',
    },
    reject: {
        method: 'POST',
        path: '/api/journals/:entryId/reject',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        body: z.object({ reason: z.string().trim().min(1).max(2000) }).strict(),
        responses: {
            200: JournalEntrySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reject a pending entry with a reason (accounting.approve).',
    },
    post: {
        method: 'POST',
        path: '/api/journals/:entryId/post',
        body: c.noBody(),
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: {
            200: JournalEntrySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post an APPROVED entry through the ledger engine (accounting.approve — 16.5). Locks it forever.',
    },
    reverse: {
        method: 'POST',
        path: '/api/journals/:entryId/reverse',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        /**
         * A23 — this used to be `c.noBody()`, and that was the defect: the
         * journal was the only reverse in the product that did not ask.
         *
         * `.trim().min(1)` and `.max(500)` are the VOUCHERS' numbers, copied
         * rather than chosen — `ReverseReceiptSchema` and `ReversePaymentSchema`
         * are both `z.string().trim().min(1, …).max(500)`, and 500 is what the
         * database CHECK on every `reversal_reason` column enforces. (FIN-02's
         * REJECT reason is 2000; that is a different act with a different
         * column, and matching it here would have made the journal's two
         * reasons disagree with each other AND with the vouchers.)
         *
         * `.trim()` runs BEFORE `.min(1)`, so "   " is a 400 and not a stored
         * blank — the same test the ReasonBox runs to keep its verb switched
         * off, so the two walls agree about what an empty answer is.
         */
        body: z
            .object({
            reason: z.string().trim().min(1, JOURNAL_MESSAGES.reverseReasonRequired).max(500),
        })
            .strict(),
        responses: {
            201: JournalEntrySchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Reverse a posted entry (accounting.approve): a NEW auto-generated entry, sides swapped, linked back (BR-FIN-02-09), carrying the REQUIRED typed reason (A23 — joining the two voucher reversals; stored on the reversing entry, audited as journal.reverse). Dated the company’s today (GEN-73). One reversal ever (FIN-02 §15); an entry a receipt or payment voucher owns is refused — reverse the voucher instead.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=journals.js.map